import React, { useMemo } from 'react';

/**
 * Confetti burst — pure CSS/React, no deps.
 * Renders a fixed-position layer of N particles with randomised
 * trajectories, colors, rotation. Re-runs when `trigger` changes.
 * Designed for ~2.5s bursts on celebratory moments.
 */
const COLORS = [
  '#FFD700', '#D4A933', '#F0D07A', // golds
  '#32D74B', '#1A9E5E',             // greens
  '#C8102E', '#FF3B30',             // reds
  '#4DA3FF', '#2E5AAC',             // blues
  '#FFFFFF', '#FF6B00',             // neutrals
];

const rand = (min, max) => Math.random() * (max - min) + min;

export const ConfettiBurst = ({ trigger = 0, count = 90, origin = 'top', zIndex = 60 }) => {
  // Regenerate particle defs when trigger flips
  const particles = useMemo(() => {
    return Array.from({ length: count }, (_, i) => {
      const isRibbon = Math.random() < 0.35;
      const w = isRibbon ? rand(5, 9) : rand(7, 12);
      const h = isRibbon ? rand(14, 22) : w * (Math.random() < 0.5 ? 1 : 0.6);
      return {
        id: i,
        left: rand(0, 100),                       // %
        xDrift: rand(-260, 260),                  // px horizontal drift
        fall: rand(360, 720),                     // px vertical fall distance
        delay: rand(0, 0.6),                       // s
        duration: rand(1.8, 3.4),                  // s
        rotate: rand(-540, 540),                   // deg
        rotateEnd: rand(-1080, 1080),
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        w, h,
        radius: isRibbon ? 2 : rand(0, w / 2),
        shape: isRibbon ? 'ribbon' : 'chip',
      };
    });
  }, [trigger, count]);

  if (!trigger) return null;

  const startTop = origin === 'top' ? '-5%' : origin === 'center' ? '40%' : '100%';

  return (
    <div
      key={trigger}
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex }}
      aria-hidden
      data-testid="confetti-burst"
    >
      {particles.map((p) => (
        <span
          key={p.id}
          className="absolute block"
          style={{
            left: `${p.left}%`,
            top: startTop,
            width: `${p.w}px`,
            height: `${p.h}px`,
            background: p.color,
            borderRadius: `${p.radius}px`,
            opacity: 0.95,
            boxShadow: `0 0 4px ${p.color}aa`,
            animation: `ewfl-confetti-fall-${p.id % 5} ${p.duration}s ${p.delay}s cubic-bezier(0.2, 0.7, 0.3, 1) forwards`,
            '--x': `${p.xDrift}px`,
            '--y': `${p.fall}px`,
            '--rot-start': `${p.rotate}deg`,
            '--rot-end': `${p.rotateEnd}deg`,
          }}
        />
      ))}
      {/* Inline keyframes — 5 variants to stagger motion curves */}
      <style>{`
        @keyframes ewfl-confetti-fall-0 { 0%{transform:translate3d(0,0,0) rotate(var(--rot-start));opacity:0} 8%{opacity:1} 100%{transform:translate3d(var(--x),var(--y),0) rotate(var(--rot-end));opacity:0} }
        @keyframes ewfl-confetti-fall-1 { 0%{transform:translate3d(0,0,0) rotate(var(--rot-start)) scaleY(0.6);opacity:0} 10%{opacity:1;transform:translate3d(calc(var(--x)*0.2),calc(var(--y)*0.1),0) rotate(calc(var(--rot-start) + 90deg)) scaleY(1)} 100%{transform:translate3d(var(--x),var(--y),0) rotate(var(--rot-end));opacity:0} }
        @keyframes ewfl-confetti-fall-2 { 0%{transform:translate3d(0,-20px,0) rotate(var(--rot-start));opacity:0} 12%{opacity:1} 60%{transform:translate3d(calc(var(--x)*0.6),calc(var(--y)*0.55),0) rotate(calc(var(--rot-end)*0.6))} 100%{transform:translate3d(var(--x),var(--y),0) rotate(var(--rot-end));opacity:0} }
        @keyframes ewfl-confetti-fall-3 { 0%{transform:translate3d(0,0,0) rotate(0deg);opacity:0} 15%{opacity:1} 40%{transform:translate3d(calc(var(--x)*0.45),calc(var(--y)*0.3),0) rotate(calc(var(--rot-end)*0.5)) scaleY(0.85)} 100%{transform:translate3d(var(--x),var(--y),0) rotate(var(--rot-end)) scaleY(1);opacity:0} }
        @keyframes ewfl-confetti-fall-4 { 0%{transform:translate3d(0,0,0) rotate(var(--rot-start)) scaleX(1);opacity:0} 10%{opacity:1} 50%{transform:translate3d(calc(var(--x)*0.5),calc(var(--y)*0.4),0) rotate(calc(var(--rot-end)*0.7)) scaleX(0.4)} 100%{transform:translate3d(var(--x),var(--y),0) rotate(var(--rot-end)) scaleX(1);opacity:0} }
      `}</style>
    </div>
  );
};
